import { useState } from "react";
import { PageLayout } from "@/components/PageLayout";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";

const timeZones = [
  { value: "UTC", label: "UTC (GMT+0)", offset: 0 },
  { value: "EST", label: "EST (GMT-5)", offset: -5 },
  { value: "CST", label: "CST (GMT-6)", offset: -6 },
  { value: "MST", label: "MST (GMT-7)", offset: -7 },
  { value: "PST", label: "PST (GMT-8)", offset: -8 },
  { value: "GMT", label: "GMT (GMT+0)", offset: 0 },
  { value: "CET", label: "CET (GMT+1)", offset: 1 },
  { value: "IST", label: "IST (GMT+5:30)", offset: 5.5 },
  { value: "JST", label: "JST (GMT+9)", offset: 9 },
  { value: "AEST", label: "AEST (GMT+10)", offset: 10 },
];

export default function TimeZoneConverter() {
  const [time, setTime] = useState("12:00");
  const [fromZone, setFromZone] = useState("UTC");
  const [toZone, setToZone] = useState("EST");
  const [result, setResult] = useState("");

  const convertTime = () => {
    const [hours, minutes] = time.split(":").map(Number);
    const fromOffset = timeZones.find(tz => tz.value === fromZone)?.offset || 0;
    const toOffset = timeZones.find(tz => tz.value === toZone)?.offset || 0;
    
    const diff = toOffset - fromOffset;
    let newHours = hours + Math.floor(diff);
    const newMinutes = minutes + ((diff % 1) * 60);
    
    if (newMinutes >= 60) {
      newHours += 1;
    } else if (newMinutes < 0) {
      newHours -= 1;
    }
    
    newHours = ((newHours % 24) + 24) % 24;
    const finalMinutes = ((newMinutes % 60) + 60) % 60;
    
    setResult(`${String(newHours).padStart(2, '0')}:${String(finalMinutes).padStart(2, '0')}`);
  };

  return (
    <PageLayout calculatorName="Time Zone Converter" calculatorPath="/calculator/time-zone">
      <div className="grid gap-8 md:grid-cols-2">
        <Card>
          <CardHeader>
            <CardTitle>Convert Time</CardTitle>
            <CardDescription>Between different time zones</CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <div>
              <Label>Time</Label>
              <Input type="time" value={time} onChange={(e) => setTime(e.target.value)} />
            </div>
            <div>
              <Label>From Time Zone</Label>
              <Select value={fromZone} onValueChange={setFromZone}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>
                  {timeZones.map(tz => (
                    <SelectItem key={tz.value} value={tz.value}>{tz.label}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label>To Time Zone</Label>
              <Select value={toZone} onValueChange={setToZone}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>
                  {timeZones.map(tz => (
                    <SelectItem key={tz.value} value={tz.value}>{tz.label}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <Button onClick={convertTime} className="w-full">Convert</Button>
          </CardContent>
        </Card>

        {result && (
          <Card>
            <CardHeader>
              <CardTitle>Converted Time</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="text-center">
                <div className="text-5xl font-bold text-primary mb-4">{result}</div>
                <div className="text-lg text-muted-foreground">
                  {timeZones.find(tz => tz.value === toZone)?.label}
                </div>
              </div>
            </CardContent>
          </Card>
        )}
      </div>
    </PageLayout>
  );
}
